import { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress, Link, Paper, Stack } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { getNearbyPubs} from '../API';
import { themeColors } from '../styles/theme';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';


interface NearbyPubsProps {
  latitude: number;
  longitude: number;
  currentPubId: string;
  radius?: number;
  preloadedPubs?: any[]; // For SEO optimisation
}

const NearbyPubs = ({ latitude, longitude, currentPubId, preloadedPubs = [] }: NearbyPubsProps) => {
  const [pubs, setPubs] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(preloadedPubs.length === 0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
      // If we have pre-loaded pubs, use them immediately
      if (preloadedPubs.length > 0) {
          setPubs(preloadedPubs);
          setIsLoading(false);
          return;
      }

      // Otherwise fetch nearby pubs
      if (latitude && longitude) {
          setIsLoading(true);
          getNearbyPubs(latitude, longitude, 1000)
              .then(response => {
                  if (response.data && response.data.pubs) {
                      // Filter out the current pub
                      const nearbyPubs = response.data.pubs
                          .filter((pub: any) => pub.google_place_id !== currentPubId)
                          .slice(0, 6); // Limit to 6 nearby pubs
                      setPubs(nearbyPubs);
                  }
              })
              .catch(error => {
                  console.error('Error fetching nearby pubs:', error);
                  setError('Could not load nearby pubs');
              })
              .finally(() => {
                  setIsLoading(false);
              });
      }
  }, [latitude, longitude, currentPubId, preloadedPubs]);

  if (isLoading) {
      return (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={24} />
          </Box>
      );
  }

  if (error) {
      return (
          <Typography color="text.secondary" sx={{ p: 2 }}>
              {error}
          </Typography>
      );
  }

  if (pubs.length === 0) {
      return (
          <Typography color="text.secondary" sx={{ p: 2 }}>
              No nearby pubs found
          </Typography>
      );
  }

  return (
      <Box >
          {pubs.map((pub: any) => (
              <Paper
                  elevation={3}
                  sx={{
                      borderRadius: 2,
                      p: 3,
                      mb: 3,
                      background: themeColors.paperYellow,
                      backdropFilter: 'blur(16px)',
                  }}
                 key={pub.google_place_id || pub.id}
              >
                  <Link
                      component={RouterLink}
                      to={`/pub/${pub.google_place_id || pub.id}`}
                      sx={{ 
                          textDecoration: 'none',
                          color: 'inherit',
                          display: 'block',
                          '&:hover': { textDecoration: 'underline' }
                      }}
                  >
                      <Stack direction={'row'} justifyContent={'space-between'}>
                          <Typography variant="subtitle1" fontWeight="bold">
                              {pub.name || pub.displayName?.text}
                          </Typography>
                          <ChevronRightIcon />
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                          {pub.address || pub.formattedAddress}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                          {Math.round(pub.distance)}m away
                      </Typography>
                  </Link>
              </Paper>
          ))}
      </Box>
  );
};

export default NearbyPubs;